import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Button, Grid, TextField, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Formik, FormikHelpers } from 'formik'
import { createProject } from '../../redux/slices/projects'
import {
  createErrorNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import projectService from '../../services/projectService'
import { Project, ProjectBaseInput } from '../../types'
import { isRequired, mustBeString } from '../../utils/validation'

interface ProjectBaseFormProps {
  beforeSubmit?: (values: ProjectBaseInput, helpers: FormikHelpers<ProjectBaseInput>) => void
  afterSubmit?: (project: Project, helpers: FormikHelpers<ProjectBaseInput>) => void
  onCancel?: () => void
}

export default function ProjectBaseForm({
  onCancel,
  beforeSubmit,
  afterSubmit
}: ProjectBaseFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The initial values.
   */
  const initialValues: ProjectBaseInput = {
    name: '',
    address: '',
    zip: '',
    city: '',
    area: 0
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('name')))
      .required(isRequired(t('name'))),
    address: Yup.string()
      .typeError(mustBeString(t('address')))
      .optional(),
    zip: Yup.string()
      .typeError(mustBeString(t('zip')))
      .required(isRequired(t('zip'))),
    city: Yup.string()
      .typeError(mustBeString(t('city')))
      .required(isRequired(t('city')))
  })

  const handleSubmit = async (
    values: ProjectBaseInput,
    { resetForm, setStatus, setSubmitting, ...helpers }: FormikHelpers<ProjectBaseInput>
  ) => {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, {
        resetForm,
        setStatus,
        setSubmitting,
        ...helpers
      })

      const createdProject: Project = await projectService.createProjectBase(values)

      dispatch(createProject(createdProject))
      resetForm()
      setStatus({ sent: true })
      dispatch(createSuccessNotification(t('projectCreateSuccess')))

      afterSubmit?.(createdProject, {
        resetForm,
        setStatus,
        setSubmitting,
        ...helpers
      })
    } catch (_) {
      setStatus({ sent: false })
      dispatch(createErrorNotification(t('projectCreateError')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                type="text"
                name="name"
                label={t('Project.name')}
                value={values.name}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                name="address"
                label={t('Project.address')}
                value={values.address}
                error={Boolean(touched.address && errors.address)}
                helperText={touched.address && errors.address}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                name="zip"
                label={t('Project.zip')}
                value={values.zip}
                error={Boolean(touched.zip && errors.zip)}
                helperText={touched.zip && errors.zip}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type="text"
                name="city"
                label={t('Project.city')}
                value={values.city}
                error={Boolean(touched.city && errors.city)}
                helperText={touched.city && errors.city}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                <Button variant="text" color="inherit" onClick={onCancel}>
                  {t('Manufacturing.cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('Manufacturing.save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  )
}
